import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Image, X, Layers, GripVertical } from 'lucide-react';
import ImageCropUploader from './ImageCropUploader';
import { getImageUrl } from '../../lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Product Item Component
const SortableProductItem = ({ product, isSelected, onSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 border rounded cursor-pointer transition-colors ${
        isSelected ? 'border-black bg-gray-50' : 'hover:bg-gray-50'
      } ${isDragging ? 'shadow-lg z-10' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
      >
        <GripVertical size={16} className="text-gray-400" />
      </div>
      <div onClick={() => onSelect(product)} className="flex items-center gap-3 flex-1 min-w-0">
        <img
          src={getImageUrl(product.image)}
          alt={product.name}
          className="w-14 h-14 rounded object-cover bg-gray-100"
          loading="eager"
          onError={(e) => { 
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/56?text=V'; 
          }}
        />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{product.name}</p>
          <p className="text-xs text-gray-500">
            {(product.images || []).length} genel • {(product.sizes || []).length} boyut
          </p>
        </div>
      </div>
    </div>
  );
};

const UnifiedImageManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddImage, setShowAddImage] = useState(false);
  const [addImageType, setAddImageType] = useState('general'); // 'general' or size like '90x90'
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadMode, setUploadMode] = useState('file');
  const [message, setMessage] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder products
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = products.findIndex(p => p.id === active.id);
      const newIndex = products.findIndex(p => p.id === over.id);
      
      const newProducts = arrayMove(products, oldIndex, newIndex);
      setProducts(newProducts);
      
      // Save new order to backend
      try {
        const productIds = newProducts.map(p => p.id);
        await fetch(`${backendUrl}/api/admin/products/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productIds)
        });
        setMessage('Sıralama kaydedildi');
        setTimeout(() => setMessage(''), 2000);
      } catch (error) {
        console.error('Reorder error:', error);
        setMessage('Sıralama kaydedilemedi');
      }
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/products`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setMessage('');
  };

  // Add general image
  const handleAddGeneralImage = async () => {
    if (!selectedProduct || !newImageUrl) return;
    
    try {
      const res = await fetch(`${backendUrl}/api/admin/products/${selectedProduct.id}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `image_url=${encodeURIComponent(newImageUrl)}`
      });
      
      if (res.ok) {
        const updatedImages = [...(selectedProduct.images || []), newImageUrl];
        setSelectedProduct({ ...selectedProduct, images: updatedImages });
        setProducts(products.map(p => p.id === selectedProduct.id ? { ...p, images: updatedImages } : p));
        setShowAddImage(false);
        setNewImageUrl('');
        setMessage('✓ Görsel eklendi');
      }
    } catch (error) {
      console.error('Error adding image:', error);
      setMessage('Hata: Görsel eklenemedi');
    }
  };

  // Add size-specific image
  const handleAddSizeImage = async (size) => {
    if (!selectedProduct || !newImageUrl) return;
    
    try {
      const res = await fetch(`${backendUrl}/api/admin/products/${selectedProduct.id}/size-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: newImageUrl, size: size })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedProduct({ ...selectedProduct, sizeImages: data.sizeImages });
        setProducts(products.map(p => p.id === selectedProduct.id ? { ...p, sizeImages: data.sizeImages } : p));
        setShowAddImage(false);
        setNewImageUrl('');
        setMessage(`✓ ${size} için görsel eklendi`);
      }
    } catch (error) {
      console.error('Error adding size image:', error);
      setMessage('Hata: Görsel eklenemedi');
    }
  };

  // Delete general image
  const handleDeleteGeneralImage = async (imageIndex) => {
    if (!selectedProduct) return;
    if (!window.confirm('Bu görseli silmek istediğinizden emin misiniz?')) return;
    
    try {
      const res = await fetch(`${backendUrl}/api/admin/products/${selectedProduct.id}/images/${imageIndex}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        const updatedImages = selectedProduct.images.filter((_, i) => i !== imageIndex);
        setSelectedProduct({ ...selectedProduct, images: updatedImages });
        setProducts(products.map(p => p.id === selectedProduct.id ? { ...p, images: updatedImages } : p));
        setMessage('✓ Görsel silindi');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  // Delete size-specific image
  const handleDeleteSizeImage = async (size, imageIndex) => {
    if (!selectedProduct) return;
    if (!window.confirm('Bu görseli silmek istediğinizden emin misiniz?')) return;
    
    try {
      const res = await fetch(
        `${backendUrl}/api/admin/products/${selectedProduct.id}/size-images/${encodeURIComponent(size)}/${imageIndex}`,
        { method: 'DELETE' }
      );
      
      if (res.ok) {
        const data = await res.json();
        setSelectedProduct({ ...selectedProduct, sizeImages: data.sizeImages });
        setProducts(products.map(p => p.id === selectedProduct.id ? { ...p, sizeImages: data.sizeImages } : p));
        setMessage('✓ Görsel silindi');
      }
    } catch (error) {
      console.error('Error deleting size image:', error);
    }
  };

  const handleAddImageSubmit = () => {
    if (addImageType === 'general') {
      handleAddGeneralImage();
    } else {
      handleAddSizeImage(addImageType);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold">Ürün Görselleri</h2>
        <p className="text-sm text-gray-500 mt-1">
          Genel görseller ve boyuta özel görselleri tek yerden yönetin. <strong>Sürükleyerek sıralayın.</strong>
        </p>
      </div>

      {message && (
        <div className={`p-3 rounded text-sm ${message.includes('✓') || message.includes('kaydedildi') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products List with Drag & Drop */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium mb-4">Ürünler ({products.length})</h3>
          <p className="text-xs text-gray-400 mb-3">⋮⋮ simgesinden tutup sürükleyin</p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={products.map(p => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2 max-h-[550px] overflow-y-auto">
                {products.map((product) => (
                  <SortableProductItem
                    key={product.id}
                    product={product}
                    isSelected={selectedProduct?.id === product.id}
                    onSelect={handleSelectProduct}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        {/* Image Management Panel */}
        <div className="lg:col-span-2 bg-white border rounded-lg p-4">
          {selectedProduct ? (
            <div className="space-y-6">
              {/* Product Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-lg">{selectedProduct.name}</h3>
                  <p className="text-sm text-gray-500">
                    Boyutlar: {(selectedProduct.sizes || []).join(', ')}
                  </p>
                </div>
                <button
                  onClick={() => { setShowAddImage(true); setAddImageType('general'); }}
                  className="flex items-center gap-2 bg-black text-white px-4 py-2 text-sm hover:bg-gray-800"
                >
                  <Plus size={16} />
                  Görsel Ekle
                </button>
              </div>

              {/* General Images Section */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Image size={18} />
                    Genel Görseller
                    <span className="text-gray-400 font-normal text-sm">
                      ({(selectedProduct.images || []).length})
                    </span>
                  </h4>
                  <button
                    onClick={() => { setShowAddImage(true); setAddImageType('general'); }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    + Ekle
                  </button>
                </div>
                
                {(selectedProduct.images || []).length > 0 ? (
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                    {(selectedProduct.images || []).map((img, idx) => (
                      <div key={idx} className="relative group aspect-square">
                        <img
                          src={getImageUrl(img)}
                          alt={`Görsel ${idx + 1}`}
                          className="w-full h-full object-cover rounded border"
                          loading="lazy"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/100'; }}
                        />
                        <button
                          onClick={() => handleDeleteGeneralImage(idx)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={12} />
                        </button>
                        {idx === 0 && (
                          <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 text-white px-1 rounded">
                            Ana
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400 text-sm border-2 border-dashed rounded">
                    Henüz genel görsel yok
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  * Boyut seçilmediğinde bu görseller gösterilir
                </p>
              </div>

              {/* Size-Specific Images Section */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Layers size={18} />
                  <h4 className="font-medium">Boyuta Özel Görseller</h4>
                </div>
                
                <div className="space-y-4">
                  {(selectedProduct.sizes || []).map((size) => {
                    const sizeImages = selectedProduct.sizeImages?.[size] || [];
                    return (
                      <div key={size} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">
                            {size} cm
                            <span className="text-gray-400 font-normal ml-2">
                              ({sizeImages.length} görsel)
                            </span>
                          </span>
                          <button
                            onClick={() => { setShowAddImage(true); setAddImageType(size); }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            + Görsel Ekle
                          </button>
                        </div>
                        
                        {sizeImages.length > 0 ? (
                          <div className="flex gap-2 flex-wrap">
                            {sizeImages.map((img, idx) => (
                              <div key={idx} className="relative group w-16 h-16">
                                <img
                                  src={getImageUrl(img)}
                                  alt={`${size} - ${idx + 1}`}
                                  className="w-full h-full object-cover rounded border"
                                  loading="lazy"
                                  onError={(e) => { e.target.src = 'https://via.placeholder.com/64'; }}
                                />
                                <button
                                  onClick={() => handleDeleteSizeImage(size, idx)}
                                  className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X size={10} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">
                            Bu boyut için görsel yok → Genel görseller gösterilir
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <Image size={48} className="mx-auto mb-2 text-gray-300" />
                <p>Düzenlemek için bir ürün seçin</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Image Modal */}
      {showAddImage && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-medium">
                {addImageType === 'general' ? 'Genel Görsel Ekle' : `${addImageType} cm için Görsel Ekle`}
              </h3>
              <button onClick={() => { setShowAddImage(false); setNewImageUrl(''); }}>
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Image Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Görsel Türü</label>
                <select
                  value={addImageType}
                  onChange={(e) => setAddImageType(e.target.value)}
                  className="w-full border px-3 py-2 text-sm rounded"
                >
                  <option value="general">Genel Görsel</option>
                  {(selectedProduct.sizes || []).map(size => (
                    <option key={size} value={size}>{size} cm için</option>
                  ))}
                </select>
              </div>

              {/* Upload Mode */}
              <div className="flex gap-2">
                <button
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 py-2 text-sm border rounded ${uploadMode === 'file' ? 'bg-black text-white' : 'hover:bg-gray-50'}`}
                >
                  Dosya Yükle
                </button>
                <button
                  onClick={() => setUploadMode('url')}
                  className={`flex-1 py-2 text-sm border rounded ${uploadMode === 'url' ? 'bg-black text-white' : 'hover:bg-gray-50'}`}
                >
                  URL ile
                </button>
              </div>
              
              {uploadMode === 'url' ? (
                <input
                  type="text"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  className="w-full border px-3 py-2 text-sm rounded"
                  placeholder="https://..."
                />
              ) : (
                <ImageCropUploader
                  accept="image/*"
                  label="Görsel Seç ve Kırp"
                  cropEnabled={true}
                  onUpload={(url) => setNewImageUrl(url)}
                />
              )}
              
              {newImageUrl && (
                <div className="border rounded p-2">
                  <p className="text-xs text-gray-500 mb-2">Önizleme:</p>
                  <img 
                    src={newImageUrl} 
                    alt="Preview" 
                    className="w-full h-32 object-contain rounded"
                  />
                </div>
              )}
              
              <button
                onClick={handleAddImageSubmit}
                disabled={!newImageUrl}
                className="w-full bg-black text-white py-2 text-sm rounded hover:bg-gray-800 disabled:bg-gray-300"
              >
                {addImageType === 'general' ? 'Genel Görsel Ekle' : `${addImageType} cm için Ekle`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedImageManager;
